import React, { useState } from 'react'
import useSWRMutation from 'swr/mutation'
import { postFetcher } from 'api/swr'
import { Button } from '@/components/ui/button'
import Loader from '@/components/ui/Loader'
import {
  ReloadIcon,
  PlusIcon,
  QuestionMarkCircledIcon,
} from '@radix-ui/react-icons'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import MyPagination from 'components/ui/MyPagination'
import { Form } from '@/components/ui/form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { MyFormInput, MyFromCheckbox, MyFormSelect } from 'components/ui/MyFormField'
import { z } from 'zod'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { mutate } from 'swr'
import { useToast } from '@/components/ui/use-toast'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const DataTableToolbar = ({
  mutate,
  nextAfter,
  after,
  goAfter,
  afters,
  sizeType,
  setSizeType,
}) => {
  return (
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <MyPagination
          nextAfter={nextAfter}
          after={after}
          goAfter={goAfter}
          afters={afters}
          className="m-0 w-auto"
        />
        <Button variant="outline" onClick={() => mutate()}>
          <ReloadIcon className="h-4 w-4 mr-2" />
          Refetch
        </Button>
        <Select
          value={sizeType || 'all'}
          onValueChange={(val) => setSizeType(val === 'all' ? null : val)}
        >
          <SelectTrigger className="w-[180px] h-9">
            <SelectValue placeholder="Size Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sizes</SelectItem>
            <SelectItem value="vertical">Vertical</SelectItem>
            <SelectItem value="square">Square</SelectItem>
            <SelectItem value="horizontal">Horizontal</SelectItem>
          </SelectContent>
        </Select>
        <HoverCard>
          <HoverCardTrigger asChild>
            <Button className="h-8 w-8 p-0" variant="ghost" aria-label="帮助">
              <QuestionMarkCircledIcon className="h-5 w-5" />
            </Button>
          </HoverCardTrigger>
          <HoverCardContent side="right" className="w-[600px]">
            <div className="space-y-2 text-sm leading-5">
              <p>
                到这里上传卡片背景图:
                <a
                  href="https://admin.stickermobi.com/ads/cdn"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline ml-1"
                >
                  https://admin.stickermobi.com/ads/cdn
                </a>
              </p>
              <p>Foreground Link, 为卡片前景图，可选</p>
              <p>Background Link, 为卡片背景图，可选，无背景用白色代替</p>
              <p>Card Width, Card Height, 为卡片宽高</p>
              <p>Avatar Width, Avatar Height, 为avatar宽高</p>
              <p>
                Avatar Position X, Avatar Position Y,
                为avatar左上角在卡片中的位置坐标
              </p>
              <p>Is Ad, 为是否需要添加广告</p>
              <p>iOS Version, 指定该卡片模板支持的最低 iOS 客户端版本号（如 1210708），老版本客户端不会展示此模版</p>
              <p>使用 Pin to Top 改变卡片展示顺序</p>
              <p>
                卡片相关api:
                <a
                  href="https://stickerstyle.feishu.cn/docx/Gf6IdmTYNoRSTVxVmrzcqfSmn4b"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline ml-1"
                >
                  https://stickerstyle.feishu.cn/docx/Gf6IdmTYNoRSTVxVmrzcqfSmn4b
                </a>
              </p>
            </div>
          </HoverCardContent>
        </HoverCard>
      </div>
      <DataTableRowCreate />
    </div>
  )
}

const DataTableRowCreate = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { toast } = useToast()
  const { trigger, isMutating, error } = useSWRMutation(
    '/avatar-card-templates',
    postFetcher,
  )

  const infoFormSchema = z
    .object({
      foregroundLink: z.string().optional(),
      backgroundLink: z.string().optional(),
      cardWidth: z.preprocess((val) => Number(val), z.number().min(1)),
      cardHeight: z.preprocess((val) => Number(val), z.number().min(1)),
      avatarWidth: z.preprocess((val) => Number(val), z.number().min(1)),
      avatarHeight: z.preprocess((val) => Number(val), z.number().min(1)),
      avatarPositionX: z.preprocess((val) => Number(val), z.number()),
      avatarPositionY: z.preprocess((val) => Number(val), z.number()),
      isAd: z.boolean().optional(),
      iosVersion: z.string().optional(),
      sizeType: z.enum(['none', 'vertical', 'square', 'horizontal']).optional(),
    })
    .refine(
      (data) => {
        const hasForeground =
          data.foregroundLink && data.foregroundLink.trim() !== ''
        const hasBackground =
          data.backgroundLink && data.backgroundLink.trim() !== ''
        return hasForeground || hasBackground
      },
      {
        message: 'At least one of foregroundLink or backgroundLink is required',
        path: ['foregroundLink'], // 错误显示在 foregroundLink 字段
      },
    )

  const form = useForm({
    resolver: zodResolver(infoFormSchema),
    defaultValues: {
      foregroundLink: '',
      backgroundLink: '',
      cardWidth: 0,
      cardHeight: 0,
      avatarWidth: 0,
      avatarHeight: 0,
      avatarPositionX: 0,
      avatarPositionY: 0,
      isAd: false,
      iosVersion: '',
      sizeType: 'none',
    },
  })

  const onSubmit = (data) => {
    if (isMutating) return
    
    // 如果 sizeType 是 'none'，则不发送该字段
    const submitData = { ...data }
    if (submitData.sizeType === 'none') {
      delete submitData.sizeType
    }

    trigger(submitData, {
      onSuccess: () => {
        mutate(['/avatar-card-templates'])
        setIsOpen(false)
        form.reset()
        toast({
          title: 'Template Created',
          description: 'The template has been successfully created.',
          className: 'bg-green-500 text-white',
        })
      },
      onError: (err) => {
        toast({
          variant: 'destructive',
          title: 'Create Failed',
          description: err.message || 'Failed to create template.',
        })
      },
    })
  }

  return (
    <div className="max-w-[500px] min-w-[130px] flex flex-col gap-2">
      <Dialog open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
        <DialogTrigger asChild>
          <Button variant="default">
            <PlusIcon className="h-4 w-4 mr-2" />
            <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">
              Create Template
            </span>
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Avatar Card Template</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <MyFormInput
                name="foregroundLink"
                form={form}
                label="Foreground Link (Optional)"
                placeholder="Enter foreground link (optional)"
              />
              <MyFormInput
                name="backgroundLink"
                form={form}
                label="Background Link (Optional)"
                placeholder="Enter background link (optional)"
              />
              <div className="text-sm text-muted-foreground">
                At least one of foregroundLink or backgroundLink is required
              </div>
              <div className="flex items-center space-x-2">
                <MyFromCheckbox name="isAd" form={form} label="Is Ad" />
              </div>
              <MyFormSelect
                name="sizeType"
                form={form}
                label="Size Type"
                items={[
                  { name: 'None', code: 'none' },
                  { name: 'Vertical', code: 'vertical' },
                  { name: 'Square', code: 'square' },
                  { name: 'Horizontal', code: 'horizontal' },
                ]}
              />
              <div className="grid grid-cols-2 gap-4">
                <MyFormInput
                  type="number"
                  name="cardWidth"
                  form={form}
                  label="Card Width"
                  placeholder="Enter card width"
                />
                <MyFormInput
                  type="number"
                  name="cardHeight"
                  form={form}
                  label="Card Height"
                  placeholder="Enter card height"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <MyFormInput
                  type="number"
                  name="avatarWidth"
                  form={form}
                  label="Avatar Width"
                  placeholder="Enter avatar width"
                />
                <MyFormInput
                  type="number"
                  name="avatarHeight"
                  form={form}
                  label="Avatar Height"
                  placeholder="Enter avatar height"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <MyFormInput
                  type="number"
                  name="avatarPositionX"
                  form={form}
                  label="Avatar Position X"
                  placeholder="Enter avatar position X"
                />
                <MyFormInput
                  type="number"
                  name="avatarPositionY"
                  form={form}
                  label="Avatar Position Y"
                  placeholder="Enter avatar position Y"
                />
              </div>
              <MyFormInput
                type="number"
                name="iosVersion"
                form={form}
                label="iOS Version (Optional)"
                placeholder="Enter iOS client version (e.g., 1210708)"
              />
              {isMutating ? (
                <Loader>Submitting...</Loader>
              ) : (
                <div className="flex gap-2">
                  <Button type="submit">Submit</Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
              )}
              {error && (
                <div className="text-red-500">
                  {error.message || 'Create failed'}
                </div>
              )}
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default DataTableToolbar
